import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { CheckCircleIcon, RocketLaunchIcon } from '@heroicons/react/24/solid';
import { ArrowRightIcon } from '@heroicons/react/24/outline';

import useDialogLauncher from '@/dialogs-routable/_hooks/useDialogLauncher';
import { DialogManager } from '@/dialogs_new/DialogManager';

import {
  BuildIcon,
  CreateOfferIcon,
  EvaluateIcon,
  FindOfferIcon,
  LightBulbActionIcon,
  PartnerIcon,
  PolishIcon,
  PublicProfileIcon,
  SiteIcon,
  StartContributingIcon,
} from './icons/ctaIcons';

const iconComponents = {
  BuildIcon,
  CreateOfferIcon,
  EvaluateIcon,
  FindOfferIcon,
  LightBulbActionIcon,
  PartnerIcon,
  PolishIcon,
  PublicProfileIcon,
  SiteIcon,
  StartContributingIcon,
  // Aliases for LightBulbAction
  CreateProjectIcon: LightBulbActionIcon,
  GetInvolvedIcon: LightBulbActionIcon,
};

CTACard.propTypes = {
  color: PropTypes.oneOf(['blue', 'orange', 'yellow']),
  state: PropTypes.oneOf(['default', 'in-progress', 'success']),
  emphasized: PropTypes.bool,
  disabled: PropTypes.bool,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  ctaLabel: PropTypes.string.isRequired,
  successCtaLabel: PropTypes.string,
  inProgressCtaLabel: PropTypes.string,
  successLabel: PropTypes.string,
  inProgressLabel: PropTypes.string,
  iconName: PropTypes.oneOf([
    'Build',
    'CreateOffer',
    'CreateProject',
    'Evaluate',
    'FindOffer',
    'GetInvolved',
    'LightBulbAction',
    'Partner',
    'Polish',
    'PublicProfile',
    'Site',
    'StartContributing',
  ]),
  // linkDestination: route ("/...") or external URL — if omitted, onCta is used
  linkDestination: PropTypes.string,
  onCta: PropTypes.func,
};

export default function CTACard(props) {
  const {
    color = 'blue',
    state = 'default',
    emphasized = false,
    disabled = false,
    title,
    description,
    ctaLabel,
    successCtaLabel,
    inProgressCtaLabel,
    successLabel = 'Completed',
    inProgressLabel = 'In progress',
    iconName = 'StartContributing',
    linkDestination,
    onCta = () => {},
  } = props;
  const isSuccess    = state === 'success';
  const isInProgress = state === 'in-progress';
  const isEmphasis   = Boolean(emphasized);

  const launch = useDialogLauncher();

  const handleDialogClick = (e) => {
    if (disabled || !linkDestination || !linkDestination.startsWith('dialog:')) return;
    const dialogKey = linkDestination.replace('dialog:', '');
    const dialog = DialogManager[dialogKey];
    if (dialog) {
      e.preventDefault();
      e.stopPropagation();
      launch(dialog.Config());
    }
  };

  const cardBg = (() => {
    if (isSuccess || disabled) return 'bg-white';
    if (isEmphasis) {
      if (color === 'blue')   return 'bg-cs-blue-50 border-2 border-cs-blue-400';
      if (color === 'orange') return 'bg-cs-orange-50 border border-cs-orange-500';
      if (color === 'yellow') return 'bg-cs-orange-50 border border-cs-yellow-500';
    }
    if (state === 'default') return 'bg-white border border-gray-100';
    return 'bg-white border border-gray-100';
  })();

  const iconBg = (() => {
    if (isSuccess) return 'bg-cs-gray-200';
    if (disabled) {
      if (color === 'blue')   return 'bg-cs-blue-500/6';
      if (color === 'orange') return 'bg-cs-orange-500/60';
      return 'bg-cs-yellow-500/60';
    }
    if (color === 'blue')   return 'bg-cs-blue-500';
    if (color === 'orange') return 'bg-cs-orange-500';
    return 'bg-cs-yellow-500';
  })();


  const showCompletedOnly = (isSuccess && successCtaLabel === 'N/A') || (isInProgress && inProgressCtaLabel === 'N/A');
  const resolvedCtaLabel  = (() => {
    if (isSuccess && successCtaLabel && successCtaLabel !== 'N/A') return successCtaLabel;
    if (isInProgress && inProgressCtaLabel && inProgressCtaLabel !== 'N/A') return inProgressCtaLabel;
    return ctaLabel;
  })();

  const ctaClassName = 'flex items-center gap-1 text-cs-blue-600 text-base leading-6 hover:text-cs-blue-500 transition-colors';

  const renderCtaContent = () => (
    <>
      {resolvedCtaLabel}
      <ArrowRightIcon className="size-5" />
    </>
  );

  const ctaLink = (() => {
    if (!linkDestination) {
      return (
        <button onClick={onCta} className={ctaClassName}>
          {renderCtaContent()}
        </button>
      );
    }

    // Internal link
    if (linkDestination.startsWith('/')) {
      return (
        <Link to={linkDestination} className={ctaClassName}>
          {renderCtaContent()}
        </Link>
      );
    }

    // Link to a dialog
    if (linkDestination.startsWith('dialog:')) {
      return (
        <button onClick={handleDialogClick} className={ctaClassName}>
          {renderCtaContent()}
        </button>
      );
    }

    // External link
    return (
      <a href={linkDestination} target="_blank" rel="noreferrer" className={ctaClassName}>
        {renderCtaContent()}
      </a>
    );
  })();

  return (
    <div
      onClick={handleDialogClick}
      className={classNames(
        'w-full sm:flex-1 sm:min-w-[240px]',
        'rounded-2xl p-3 flex flex-col',
        cardBg,
        !disabled && 'hover:shadow-md transition-shadow duration-200 cursor-pointer',
        disabled && 'opacity-40 cursor-not-allowed pointer-events-none',
      )}
    >
      {/* Icon area */}
      <div className={classNames(
        'relative h-[136px] w-full rounded-[10px] flex-shrink-0',
        iconBg,
        'flex items-center justify-center overflow-hidden p-2',
      )}>
        <div className="p-4 rounded-full">
          {(() => {
            const Icon = iconComponents[`${iconName}Icon`];

            if (!Icon) return null;
            return <Icon size={64} isSuccess={isSuccess} className="opacity-90" />;
          })()}
        </div>

        {isInProgress && (
          <div className="absolute top-2 right-2 flex items-center gap-[4px] bg-cs-purple-500 border-2 border-purple-200 rounded-[10px] px-[8px] py-[4px]">
            <RocketLaunchIcon className="size-[14px] text-white flex-shrink-0" aria-hidden="true" />
            <span className="font-normal text-sm text-white tracking-[0.05px] leading-4 whitespace-nowrap">
              {inProgressLabel}
            </span>
          </div>
        )}

        {isSuccess && (
          <div className="absolute top-2 right-2 flex items-center gap-[4px] bg-green-500 ring-1 ring-white rounded-full px-[8px] py-[4px]">
            <CheckCircleIcon className="size-[14px] text-white flex-shrink-0" aria-hidden="true" />
            <span className="font-normal text-sm text-white tracking-[0.05px] leading-4 whitespace-nowrap">
              {successLabel}
            </span>
          </div>
        )}
      </div>

      {/* Text + action */}
      <div className="flex flex-col flex-1 pt-4">
        <div className="flex flex-col gap-[12px] flex-1">
          <p className="font-semibold text-base leading-[1.2] text-gray-700">{title}</p>
          {description && (
            <p className="font-normal text-sm text-gray-700">{description}</p>
          )}
        </div>

        <div className="mt-auto pt-4">
          {showCompletedOnly ? (
            <span className="text-xs text-gray-700 font-medium flex items-center gap-1">
              {isSuccess ? (
                <CheckCircleIcon className="size-3.5 text-green-500 flex-shrink-0" aria-hidden="true" />
              ) : (
                <RocketLaunchIcon className="size-3.5 text-cs-purple-500 flex-shrink-0" aria-hidden="true" />
              )}
              {isSuccess ? successLabel : inProgressLabel}
            </span>
          ) : ctaLink}
        </div>
      </div>
    </div>
  );
}
